import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Layout from "@/components/Layout";
import Streams from "@/pages/Streams";
import StreamDetail from "@/pages/StreamDetail";
import Analytics from "@/pages/Analytics";
import Videos from "@/pages/Videos";
import Players from "@/pages/Players";
import PlayerDetail from "@/pages/PlayerDetail";
import Feeds from "@/pages/Feeds";
import Branding from "@/pages/Branding";
import EmbedPlayer from "@/pages/EmbedPlayer";
import EmbedLibrary from "@/pages/EmbedLibrary";
import ChurchEmbed from "@/pages/ChurchEmbed";
import LiveEmbed from "@/pages/LiveEmbed";
import Playlists from "@/pages/Playlists";
import AdminUsers from "@/pages/AdminUsers";
import ClientDetail from "@/pages/ClientDetail";
import ChurchBranding from "@/pages/ChurchBranding";
import Billing from "@/pages/Billing";
import BillingThankYou from "@/pages/BillingThankYou";
import Subscriptions from "@/pages/Subscriptions";
import ChurchProfile from "@/pages/ChurchProfile";
import Placeholder from "@/pages/Placeholder";
import SubAccounts from "@/pages/SubAccounts";
import UploadProfiles from "@/pages/UploadProfiles";
import Storage from "@/pages/Storage";
import Usage from "@/pages/Usage";
import SupportInbox from "@/pages/SupportInbox";
import Support from "@/pages/Support";
import SupportTickets from "@/pages/SupportTickets";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import Contact from "@/pages/Contact";
import ChannelBuilder from "@/pages/ChannelBuilder";
import Crm from "@/pages/Crm";
import LinearTv from "@/pages/LinearTv";
import CookieConsent from "@/components/CookieConsent";

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/streams" replace />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { user } = useAuth();
  if (user && !user.is_super_admin) return <Navigate to="/streams" replace />;
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/embed/player/:id" element={<EmbedPlayer />} />
      <Route path="/embed/library/:ownerId" element={<EmbedLibrary />} />
      <Route path="/embed/church/:code" element={<ChurchEmbed />} />
      <Route path="/embed/live/:id" element={<LiveEmbed />} />
      <Route element={<Layout />}>
        <Route path="/streams" element={<Streams />} />
        <Route path="/streams/:id" element={<StreamDetail />} />
        <Route path="/videos" element={<Videos />} />
        <Route path="/playlists" element={<Playlists />} />
        <Route path="/players" element={<Players />} />
        <Route path="/players/:id" element={<PlayerDetail />} />
        <Route path="/feeds" element={<Feeds />} />
        <Route path="/branding" element={<Branding />} />
        <Route path="/profile" element={<ChurchProfile />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/billing/thank-you" element={<BillingThankYou />} />
        <Route path="/usage" element={<Usage />} />
        <Route path="/team" element={<AdminRoute><AdminUsers /></AdminRoute>} />
        <Route path="/clients/:id" element={<AdminRoute><ClientDetail /></AdminRoute>} />
        <Route path="/sub-accounts" element={<AdminRoute><SubAccounts /></AdminRoute>} />
        <Route path="/upload-profiles" element={<AdminRoute><UploadProfiles /></AdminRoute>} />
        <Route path="/storage" element={<AdminRoute><Storage /></AdminRoute>} />
        <Route path="/church-branding" element={<AdminRoute><ChurchBranding /></AdminRoute>} />
        <Route path="/support-inbox" element={<AdminRoute><SupportInbox /></AdminRoute>} />
        <Route path="/support" element={<Support />} />
        <Route path="/support-tickets" element={<AdminRoute><SupportTickets /></AdminRoute>} />
        <Route path="/subscriptions" element={<AdminRoute><Subscriptions /></AdminRoute>} />
        <Route path="/channel-builder" element={<SuperAdminRoute><ChannelBuilder /></SuperAdminRoute>} />
        <Route path="/crm" element={<SuperAdminRoute><Crm /></SuperAdminRoute>} />
        <Route path="/linear-tv" element={<SuperAdminRoute><LinearTv /></SuperAdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/streams" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <CookieConsent />
          <Toaster theme="dark" position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
